import { type Thread, type ThreadListFilter, useStores } from '@littlebird/sdk';
import { usePagedLoad } from '@littlebird/ui-utils';
import { useNavigate } from '@tanstack/react-router';
import { observer } from 'mobx-react-lite';
import { AnimatePresence, motion } from 'motion/react';
import { useCallback, useRef, type MouseEvent } from 'react';
import { VList, type VListHandle } from 'virtua';

import { Icons } from '@/assets/icons/icons';
import { YStack } from '@/components/ui';
import { Empty } from '@/components/ui/EmptyState';
import { Page } from '@/design-system-v2/page';
import { useChatListHiddenChatIds } from '@/features/chat/chat-list-hidden-chats';
import { withoutHiddenAgentChats } from '@/features/home/proactive-agent-visibility';
import { isNewTabClick, useTabs } from '@/features/tabs';
import { useEndReachedLoadMore } from '@/hooks/use-end-reached-load-more';
import { useScrollFadeForOffset } from '@/hooks/useScrollFade';
import { addSearchFocus } from '@/lib/search-focus';
import { cn } from '@/lib/utils';
import { trackEvent } from '@/utils/analytics';

import {
  useChatBulkSelection,
  useChatListPagination,
  useChatSearchResults,
} from './all-chats.hooks';
import {
  type ChatListItem,
  type ChatSearchResult,
  getChatListItemKey,
  getSearchResultThreadId,
} from './all-chats.types';
import { filterChatsByTitle, isFullTextSearchQuery } from './all-chats.utils';
import { ChatBulkActions } from './ChatBulkActions';
import { ChatRow } from './ChatRow';
import { ChatSearchResultRow } from './ChatSearchResultRow';

const CHAT_LIST_PREFETCH_TAIL_COUNT = 5;
const LOADING_MORE_DOT_INDICES = [0, 1, 2];

export const AllChatsList = observer(function AllChatsList({
  query,
  filter,
}: {
  query: string;
  filter: ThreadListFilter;
}) {
  const navigate = useNavigate();
  const { openTab } = useTabs();
  const { threads } = useStores();
  const hiddenChatIds = useChatListHiddenChatIds();
  const listRef = useRef<VListHandle>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const trimmedQuery = query.trim();
  const isSearching = isFullTextSearchQuery(trimmedQuery);
  const archiveMode = filter === 'archived' ? 'unarchive' : 'archive';
  const chatPagination = useChatListPagination(filter);
  const { results, isLoading: isSearchLoading } = useChatSearchResults(
    trimmedQuery,
    isSearching
  );
  const searchResults = withoutHiddenAgentChats(
    results,
    hiddenChatIds,
    getSearchResultThreadId
  );

  const visibleChats = withoutHiddenAgentChats(
    threads.getChatsForFilter(filter),
    hiddenChatIds,
    (thread) => thread.id
  );
  const filteredChats = isSearching
    ? []
    : filterChatsByTitle(visibleChats, trimmedQuery);
  const listItems: ChatListItem[] = isSearching
    ? searchResults.map((result) => ({ type: 'search-result', result }))
    : filteredChats.map((thread) => ({ type: 'chat', thread }));

  // Keep selections valid across browse and search views so query changes do
  // not silently discard an in-progress bulk operation
  const selectableChatIds = [
    ...visibleChats.map((chat) => chat.id),
    ...searchResults.map((result) => getSearchResultThreadId(result)),
  ];
  const selection = useChatBulkSelection(selectableChatIds, archiveMode);

  const openChat = useCallback(
    (thread: Thread, event?: MouseEvent) => {
      trackEvent('ALL_CHATS_CHAT_OPENED');
      const path = `/chats/${thread.id}`;
      if (event && isNewTabClick(event)) {
        openTab({ path });
        return;
      }
      navigate({ to: '/chats/$chatId', params: { chatId: thread.id } });
    },
    [navigate, openTab]
  );

  const openSearchResult = useCallback(
    (item: ChatSearchResult, event?: MouseEvent) => {
      trackEvent('ALL_CHATS_CHAT_OPENED');
      const chatId = getSearchResultThreadId(item);
      if (event && isNewTabClick(event)) {
        openTab({ path: `/chats/${chatId}` });
        return;
      }
      if (item.type === 'message') {
        navigate({
          to: '/chats/$chatId',
          params: { chatId },
          search: addSearchFocus(undefined, {
            query: trimmedQuery,
            bucketId: item.id,
            threadId: chatId,
            occurrence: 0,
          }),
        });
        return;
      }
      navigate({ to: '/chats/$chatId', params: { chatId } });
    },
    [navigate, openTab, trimmedQuery]
  );

  const { runLoadMore, isLoadingMore } = usePagedLoad({
    enabled: chatPagination.isLoaded && !isSearching,
    canLoadMore: chatPagination.hasMore,
    loadPage: chatPagination.loadNextPage,
  });
  const isNearEnd = useCallback(() => {
    if (filteredChats.length === 0) return false;
    const list = listRef.current;
    if (!list) return false;
    return (
      list.findEndIndex() >=
      filteredChats.length - CHAT_LIST_PREFETCH_TAIL_COUNT
    );
  }, [filteredChats.length]);
  const { check: checkEnd } = useEndReachedLoadMore({
    isLoadingMore,
    runLoadMore,
    isNearEnd,
  });
  const onScrollOffset = useScrollFadeForOffset(containerRef);
  const handleListScroll = useCallback(
    (offset: number) => {
      onScrollOffset(offset);
      if (!isSearching) checkEnd();
    },
    [onScrollOffset, checkEnd, isSearching]
  );

  if (!chatPagination.isLoaded) return null;
  if (visibleChats.length === 0 && filter === 'all') {
    return (
      <YStack className="flex-1 min-h-0 items-center justify-center">
        <EmptyChats />
      </YStack>
    );
  }
  if (listItems.length === 0 && !isSearchLoading) {
    return (
      <YStack className="flex-1 min-h-0 items-center justify-center">
        <NoMatchingChats hasFilter={filter !== 'all'} />
      </YStack>
    );
  }

  return (
    <YStack ref={containerRef} className="flex-1 min-h-0 relative">
      <VList
        key={isSearching ? 'search' : 'browse'}
        ref={listRef}
        overscan={10}
        onScroll={handleListScroll}
        className={cn('scrollbar-hover flex-1 pb-16', Page.topPadding)}
      >
        {listItems.map((item) => (
          <div
            key={getChatListItemKey(item)}
            className={cn(Page.column(), 'pb-1')}
          >
            {item.type === 'chat' ? (
              <ChatRow
                thread={item.thread}
                isSelected={selection.selectedIds.has(item.thread.id)}
                selectionActive={selection.selectionActive}
                onToggleSelect={selection.toggleSelect}
                onOpen={openChat}
              />
            ) : (
              <ChatSearchResultRow
                item={item.result}
                query={trimmedQuery}
                isSelected={selection.selectedIds.has(
                  getSearchResultThreadId(item.result)
                )}
                selectionActive={selection.selectionActive}
                onToggleSelect={selection.toggleSelect}
                onOpen={openSearchResult}
              />
            )}
          </div>
        ))}
      </VList>

      <AnimatePresence>
        {isLoadingMore && !isSearching && (
          <motion.div
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 8 }}
            transition={{ duration: 0.15 }}
            className="absolute bottom-4 left-0 right-0 flex justify-center pointer-events-none z-[5]"
          >
            <div className="flex h-8 w-11 items-center justify-center rounded-full border border-border bg-surface text-foreground shadow-lg">
              <div className="flex items-center gap-0.5">
                {LOADING_MORE_DOT_INDICES.map((dotIndex) => (
                  <span
                    key={dotIndex}
                    className="size-1 rounded-full bg-current animate-typing-dot inline-block"
                    style={{ animationDelay: `${dotIndex * 0.15}s` }}
                  />
                ))}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <ChatBulkActions
        selectedIds={selection.selectedIds}
        confirmingDelete={selection.confirmingDelete}
        isArchiving={selection.isArchiving}
        isDeleting={selection.isDeleting}
        archiveMode={archiveMode}
        onClear={selection.clearSelection}
        onArchiveOrRestore={selection.handleBulkArchiveOrRestore}
        onDelete={selection.handleBulkDelete}
      />
    </YStack>
  );
});

function EmptyChats() {
  return (
    <Empty.Root variant="plain">
      <Empty.Media>
        <Icons.newChat />
      </Empty.Media>
      <Empty.Header>
        <Empty.Title>No chats yet</Empty.Title>
        <Empty.Description>Start a new chat to see it here.</Empty.Description>
      </Empty.Header>
    </Empty.Root>
  );
}

function NoMatchingChats({ hasFilter }: { hasFilter: boolean }) {
  return (
    <Empty.Root variant="plain">
      <Empty.Media>
        <Icons.search />
      </Empty.Media>
      <Empty.Header>
        <Empty.Title>No matching chats</Empty.Title>
        <Empty.Description>
          {hasFilter
            ? 'Try a different search term or filter.'
            : 'Try a different search term.'}
        </Empty.Description>
      </Empty.Header>
    </Empty.Root>
  );
}
